<template>
  <v-container class="toast_container">
      <v-snackbar
        v-for="(msg, index) in toastEvent.messages"
        :key="msg.id"
        v-model="activeToasts[msg.id]"
        :style="{ top: `${index * 60}px` }"
        location="top right"
        color="transparent"
        variant="outlined"
        class="toast_alert_container pa-0 ma-0 lang_translate"
      >
        <v-alert
          class="toast_alert font-weight-light lang_translate"
          @click="handleToastClick(msg.id)"
          closable
          :text="msg.text"
          border="start"
          :color="msg.type"
          style="color: white !important"
        ></v-alert>
      </v-snackbar>
  </v-container>
</template>

<script lang="ts" setup>
import { ref, watchEffect } from 'vue';
import { toastEvent, removeToast } from '@/services/notifications/toastService';

// Track active toasts
const activeToasts = ref<{ [id: number]: boolean }>({});
const toastTimers = ref<{ [id: number]: ReturnType<typeof setTimeout> }>({});

// Watch for new toasts and set up individual timeouts only for success messages
watchEffect(() => {
  toastEvent.messages.forEach((msg) => {
    if (!activeToasts.value[msg.id]) {
      activeToasts.value[msg.id] = true;
      console.log(`🚀 Toast #${msg.id} appeared with timeout: ${msg.timeout === -1 ? "Persistent (No Timeout)" : msg.timeout + "ms"}`);

      // Only apply timeout for success messages
      if (msg.timeout !== -1) {
        toastTimers.value[msg.id] = setTimeout(() => {
          handleTimeout(msg.id);
        }, msg.timeout);
      }
    }
  });
});

// Handles timeout removal
const handleTimeout = (id: number) => {
  console.groupCollapsed(`⏳ [Toast Timeout] ID: ${id}`);
  console.log(`🔔 Toast #${id} reached its timeout and will now be removed.`);
  console.groupEnd();

  clearTimeout(toastTimers.value[id]); // Ensure timer is cleared
  delete toastTimers.value[id]; // Remove timer reference
  delete activeToasts.value[id]; // Remove toast from UI
  removeToast(id); // Remove from reactive state
};

// Click to remove manually
const handleToastClick = (id: number) => {
  console.groupCollapsed(`🖱️ [Toast Clicked] ID: ${id}`);
  console.log(`🛑 Toast #${id} manually closed by user.`);
  console.groupEnd();

  clearTimeout(toastTimers.value[id]); // Clear the timeout
  delete toastTimers.value[id]; // Remove timer reference
  delete activeToasts.value[id]; // Remove toast from UI
  removeToast(id); // Remove from state
};
</script>

<style lang="scss">
// Hide Ghost Container
.v-container.v-locale--is-ltr.toast_container {
  display: none !important;
}

// Toast alert styles
.toast_alert_container {
  
  .v-snackbar__wrapper {
    min-width: 325px;
    top: 75px;
    right: 10px;
    height: 55px;
  }
  
  .v-snackbar__content {
    padding: 0px;
    max-width: 325px;
  }
  
  // Reduce padding on the toast container
  .v-alert {
    padding: 10px;
    max-width: 325px;
    min-height: 40px;
  }
}

// Adjust toast positioning for mobile screens
@media (max-width: 767px) {
  
  .toast_alert_container {
    width: 100%;
    justify-content: center !important;
    
    .v-snackbar__wrapper {
      width: 100%;
      height: 40px;
      max-width: 95vw;
      // bottom: 75px !important; // Uncomment to move toasts back to bottom on mobile
      // top: unset; // Uncomment to move toasts back to bottom on mobile
      right: unset;
    }
    
    .v-snackbar__content {
      max-width: 100%;
    }
    
    .v-alert {
      width: 100%;
      max-width: none;
    }
  }
}

</style>